import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import apiClient from "@shared/services/apiClient";

interface ReportOption {
  id: string;
  label: string;
  report_id: string;
  report_type: string;
  status: string;
  patient_name: string;
  mrn: string;
  report_date: string;
}

interface Props {
  value: string;
  onChange: (reportId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const AsyncReportSelect: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const { t } = useTranslation("features");
  const [options, setOptions] = useState<ReportOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get("/reports?limit=50")
      .then((res) => {
        if (cancelled) return;
        const reports: any[] = Array.isArray(res.data)
          ? res.data
          : res.data?.reports ?? [];
        setOptions(
          reports.map((r) => {
            const patient = r.eeg_study?.patient;
            const patientName = patient
              ? `${patient.last_name ?? ""}, ${patient.first_name ?? ""}`.trim().replace(/^,\s*/, "")
              : "Unknown";
            const mrn = patient?.mrn ?? "—";
            const date = r.created_date
              ? new Date(r.created_date).toLocaleDateString()
              : "—";
            const type = r.report_type ?? "Report";
            const status = r.status ?? "draft";
            return {
              id: r.report_id ?? r.id,
              report_id: r.report_id ?? r.id,
              label: `${patientName} — ${type} — ${date} [${status}]`,
              report_type: type,
              status,
              patient_name: patientName,
              mrn,
              report_date: date,
            };
          }),
        );
      })
      .catch(() => setOptions([]))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selected = value ? options.filter((o) => o.report_id === value) : [];

  return (
    <Typeahead
      id="async-report-select"
      options={options}
      selected={selected}
      onChange={(items) => {
        const item = items[0] as ReportOption | undefined;
        onChange(item?.report_id ?? "");
      }}
      labelKey="label"
      filterBy={["label", "patient_name", "mrn", "report_type", "report_date"]}
      placeholder={
        placeholder ||
        t("ui.patientSearch.reportPlaceholder", {
          defaultValue: "Search by patient name, MRN, or report type...",
        })
      }
      isLoading={loading}
      disabled={disabled}
      clearButton
      minLength={0}
      renderMenuItemChildren={(option) => {
        const o = option as ReportOption;
        return (
          <div>
            <strong>{o.patient_name}</strong>
            <div className="small text-muted">
              <Trans ns="features" i18nKey="ui.copy.4f63f4044a5f" />{o.mrn} · {o.report_type} · {o.report_date}
              <span
                className={`ms-2 badge ${
                  o.status === "signed"
                    ? "bg-success"
                    : o.status === "draft"
                      ? "bg-warning text-dark"
                      : "bg-secondary"
                }`}
              >
                {o.status}
              </span>
            </div>
          </div>
        );
      }}
    />
  );
};

export default AsyncReportSelect;
